import React, { useEffect, useRef, useState } from 'react'

import { HOTSPOT_COPY, PANEL_CLOSE_LABEL, SPEC_NOTE, type HotspotId } from '../twinContent'
import { CompareBars } from './CompareBars'

type Props = {
  /** null closes the panel; the markup stays mounted so the slide-out transition can play. */
  id: HotspotId | null
  onClose: () => void
}

export function TwinInfoPanel({ id, onClose }: Props) {
  const panel = useRef<HTMLDivElement>(null)
  const opener = useRef<Element | null>(null)

  useEffect(() => {
    const el = panel.current
    if (!el) return
    if (id) {
      opener.current = document.activeElement
      el.focus({ preventScroll: true })
      return
    }
    // Only pull focus back if it is still inside the panel - otherwise we would yank it away from
    // wherever the reader has since moved on to.
    const from = opener.current
    opener.current = null
    if (from instanceof HTMLElement && el.contains(document.activeElement)) from.focus()
  }, [id])

  // Press anywhere outside to dismiss. Bound only while open, and attached in an effect - which
  // runs after the click that opened the panel, so that click cannot close it again.
  useEffect(() => {
    if (!id) return
    const root = panel.current?.closest('#vr-twin')
    if (!root) return

    const onPointerDown = (e: Event) => {
      const target = e.target as Element | null
      if (!target || panel.current?.contains(target)) return
      // A press on another marker swaps the panel's contents; closing here would slide it out and
      // straight back in.
      if (target.closest?.('#hotspot-layer')) return
      onClose()
    }

    root.addEventListener('pointerdown', onPointerDown)
    return () => root.removeEventListener('pointerdown', onPointerDown)
  }, [id, onClose])

  // `id` clears the moment the panel is dismissed, but the slide-out takes 300ms - so render the
  // last hotspot rather than emptying mid-transition, which left the bordered spec list on its own.
  const [shown, setShown] = useState<HotspotId | null>(null)
  useEffect(() => {
    if (id) setShown(id)
  }, [id])

  const copy = shown ? HOTSPOT_COPY[shown] : null

  return (
    <div
      id="info-panel"
      className={id ? 'open' : undefined}
      ref={panel}
      role="region"
      aria-labelledby="panel-title"
      tabIndex={-1}
    >
      <button className="close" id="panel-close" type="button" aria-label={PANEL_CLOSE_LABEL} onClick={onClose}>
        &#10005;
      </button>
      {/* This grows to fill the panel, which keeps the note below on its bottom edge when a hotspot
          has little to say, and directly under the content when it has a lot. */}
      <div className="panel-flow">
        <h2 id="panel-title">{copy?.title}</h2>
        <p id="panel-body">{copy?.body}</p>
        {copy?.specs?.length ? (
          <ul className="specs" id="panel-specs">
            {copy.specs.map(([label, value]) => (
              <li key={label}>
                <span>{label}</span>
                <span>{value}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <div id="panel-compare">
          {copy?.compare ? <CompareBars note={copy.compare.note} rows={copy.compare.rows} /> : null}
        </div>
      </div>
      <div className="spec-note">{SPEC_NOTE}</div>
    </div>
  )
}
